import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  makeStyles,
  Radio,
  RadioGroup
} from "@material-ui/core";
import {
  expressFleetConfigListUrl,
  expressFleetOpsPageUrl
} from "@saleor/autobooking/urls";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import { configurationOpsMenuUrl } from "@saleor/configuration_operations";
import useNavigator from "@saleor/hooks/useNavigator";
import { maybe } from "@saleor/misc";
import useMallSearch from "@saleor/searches/useMallSearch";
import { getMallChoices } from "@saleor/staff/utils";
import {
  ActivityLogFormatEnum,
  ActivityLogSortField,
  OrderDirection,
  SmopStatusEnum,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import moment from "moment";
import * as React from "react";

import RateEventItem from "../../components/RateEventItem";
import { useLogisticsActivityLogs } from "../../queries";
import { LogisticsActivityLogs_logisticsActivityLogs_edges_node } from "../../types/LogisticsActivityLogs";
import { AuditLogsUrlQueryParams } from "../../urls";

const useStyles = makeStyles(
  _ => ({
    borderBottom: {
      borderBottom: "solid #CED3DD 0.5px",
      paddingLeft: "35px"
    },
    filterContainer: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      paddingLeft: "10px"
    },
    filterMall: {
      borderRight: "solid #CED3DD 1px",
      marginRight: "5px",
      minWidth: "250px",
      paddingRight: "20px"
    },
    filterStoreType: {
      paddingLeft: "15px",
      width: "535px"
    },
    loadMoreButton: {
      border: "1px solid #CED3DD40",
      borderRadius: "50px",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
      height: "40px",
      textTransform: "none",
      width: "185px"
    },
    pageHeader: {
      display: "flex",
      flexDirection: "row",
      marginTop: "10px"
    },
    textCenter: {
      textAlign: "center"
    }
  }),
  { name: "AuditLogsExpressFleet" }
);

interface AuditLogsExpressFleetProps {
  id?: string;
  params: AuditLogsUrlQueryParams;
  pageName: string;
  cardTitle: string;
}

const AuditLogsExpressFleet: React.FC<AuditLogsExpressFleetProps> = props => {
  const { id, pageName, cardTitle } = props;
  const classes = useStyles({});
  const navigate = useNavigator();

  const [type, setType] = React.useState("");
  const [mallValue, setMallValue] = React.useState("");
  const [mallLabel, setMallLabel] = React.useState("");

  const breadcrumbDirectory = [
    {
      name: "Configuration",
      url: configurationOpsMenuUrl
    },
    {
      name: "Express Fleet",
      url: id ? expressFleetOpsPageUrl : expressFleetConfigListUrl
    },
    {
      name: pageName
    }
  ];

  const sortBy = {
    sortBy: {
      direction: OrderDirection.DESC,
      field: ActivityLogSortField.CREATED_AT
    }
  };

  const { data, loading, loadMore } = useLogisticsActivityLogs({
    displayLoader: true,
    variables: {
      ...sortBy,
      first: 100,
      format: ActivityLogFormatEnum.NEW,
      ...(id ? { malls: [id] } : mallValue ? { malls: [mallValue] } : {}),
      ...(type.length > 0 ? { type: type as StoreTypeEnum } : {})
    }
  });
  const logs = maybe(() =>
    data.logisticsActivityLogs.edges.map(edges => edges.node)
  );
  const onFetchMore = () => {
    loadMore(
      (prev, next) => {
        if (
          prev.logisticsActivityLogs.pageInfo.endCursor ===
          next.logisticsActivityLogs.pageInfo.endCursor
        ) {
          return prev;
        }
        return {
          ...prev,
          logisticsActivityLogs: {
            ...prev.logisticsActivityLogs,
            edges: [
              ...prev.logisticsActivityLogs.edges,
              ...next.logisticsActivityLogs.edges
            ],
            pageInfo: next.logisticsActivityLogs.pageInfo
          }
        };
      },
      {
        after: data.logisticsActivityLogs.pageInfo.endCursor
      }
    );
  };
  const hasMore = maybe(
    () => data.logisticsActivityLogs.pageInfo.hasNextPage,
    false
  );

  const {
    loadMore: loadMoreMall,
    search: searchMalls,
    result: searchMallsOpts
  } = useMallSearch({
    skip: !!id,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: SmopStatusEnum.ACT,
      isServiceable: true
    }
  });
  const malls = maybe(() => searchMallsOpts?.data?.search?.edges, []).map(
    edge => edge.node
  );
  const mallChoices = [
    { label: "All SM Serviceable Malls", value: "" },
    ...getMallChoices(malls)
  ];

  const getDateOnly = (isoString: string) =>
    moment
      .utc(isoString)
      .local()
      .format("YYYY-MM-DD");

  const groupByDate = (
    events: LogisticsActivityLogs_logisticsActivityLogs_edges_node[]
  ) =>
    events &&
    events.length > 0 &&
    events.reduce((grouped: Record<string, any[]>, event) => {
      const date = getDateOnly(event.createdAt);
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
      return grouped;
    }, {});
  const groupedEvents = logs && logs.length > 0 ? groupByDate(logs) : [];

  const onBack = () => {
    navigate(id ? expressFleetOpsPageUrl : expressFleetConfigListUrl);
  };

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <div className={classes.pageHeader}>
        <PageBackNavigation onBack={() => onBack()} />
        <PageHeader title={pageName}></PageHeader>
      </div>

      <Card>
        <CardHeader
          className={classes.borderBottom}
          title={cardTitle}
        ></CardHeader>
        <CardContent>
          <div className={classes.filterContainer}>
            {!id && (
              <SingleAutocompleteSelectField
                className={classes.filterMall}
                idInputTxt="auditLogsExpressFleetSelectMall"
                choices={maybe(() => mallChoices, [])}
                label={"All SM Serviceable Malls"}
                name="mallId"
                value={mallValue}
                displayValue={mallLabel}
                onChange={event => {
                  if (event.target.value === "") {
                    setMallValue("");
                    setMallLabel("");
                  } else {
                    const choice = mallChoices.find(
                      category => category.value === event.target.value
                    );
                    setMallValue(choice.value);
                    setMallLabel(choice.label);
                  }
                }}
                fetchChoices={query => searchMalls(query)}
                hasMore={maybe(
                  () => searchMallsOpts.data.search.pageInfo.hasNextPage
                )}
                loading={searchMallsOpts.loading}
                onFetchMore={loadMoreMall}
              />
            )}
            <FormControl
              component="fieldset"
              className={classes.filterStoreType}
            >
              <RadioGroup
                row
                defaultValue={""}
                aria-label="filter"
                name="customized-radios"
                onChange={event => setType(event.target.value)}
              >
                <FormControlLabel
                  value={""}
                  control={<Radio color="primary" />}
                  label="All"
                />
                <FormControlLabel
                  value={StoreTypeEnum.FOOD_AND_BEVERAGES}
                  control={<Radio color="primary" />}
                  label="Food & Beverage (F&B)"
                />
                <FormControlLabel
                  value={StoreTypeEnum.GENERAL_MERCHANDISE}
                  control={<Radio color="primary" />}
                  label="General Merchandise (GM)"
                />
              </RadioGroup>
            </FormControl>
          </div>

          {logs && logs.length === 0 && !loading && (
            <div className={classes.textCenter}>No records found.</div>
          )}

          {logs && logs.length > 0 && (
            <>
              {Object.entries(groupedEvents).map(
                ([date, eventsByDate], index) => (
                  <React.Fragment
                    key={`auditLogsExpressFleetEvent-${index}-${date}`}
                  >
                    {date && eventsByDate && (
                      <RateEventItem
                        type={"EXPRESS_FLEET"}
                        mallId={id}
                        filterBulkMallId={mallValue}
                        date={date}
                        eventsByDate={eventsByDate}
                      />
                    )}
                  </React.Fragment>
                )
              )}

              {!loading && hasMore && (
                <div className={classes.textCenter}>
                  <Button
                    className={classes.loadMoreButton}
                    color="primary"
                    onClick={onFetchMore}
                  >
                    Load More Activities
                  </Button>
                </div>
              )}
            </>
          )}

          {loading && (
            <div className={classes.textCenter}>
              <CircularProgress />
            </div>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AuditLogsExpressFleet;
